import { safeSubmitForm } from "~/jose_modern/auto_submit.js";
import { onceEach } from "~/jose_modern/once.js";
import { setTagInputSelection } from "~/recipal-app/controllers/shared/tag_input.js";

/*
 * Collaborating partial: app/views/recipes/_ingredient_filter_controls.html.erb
 * - Reads data-scopes-allowing-blank and data-current-scope
 */
const initializeIngredientSearch = (container) => {
  const filterSelector = "[data-scopes-allowing-blank]";
  const filterContainers = container.querySelectorAll
    ? [...container.querySelectorAll(filterSelector)]
    : [];
  if (container.matches?.(filterSelector)) {
    filterContainers.push(container);
  }

  /*
   * Force submit when re-clicking an already-selected filter.
   * Radio buttons don't fire 'change' when clicking the already-selected option,
   * so we handle this case manually.
   */
  onceEach(filterContainers, "ingredient-search-filters", (filterContainer) => {
    const radioButtons = filterContainer.querySelectorAll("input[type=radio][name=scope_filter]");
    radioButtons.forEach((radio) => {
      radio.addEventListener("click", (event) => {
        const target = event.target;
        const scopeContainer = target.closest("[data-current-scope]");
        const wasAlreadySelected = scopeContainer.dataset.currentScope === target.value;

        if (wasAlreadySelected && target.form) {
          safeSubmitForm(target.form);
        }

        // Update save-default-filter buttons visibility and tippy state
        const saveButtons = document.querySelectorAll(".save-default-filter-btn");
        saveButtons.forEach((btn) => {
          const matchesSelected = btn.id === `save-default-${target.value}-btn`;
          btn.classList.toggle("is-hidden", !matchesSelected);

          // Disable/enable tooltip based on button visibility
          if (btn._tippy) {
            if (!matchesSelected) {
              btn._tippy.hide();
              btn._tippy.disable();
            } else {
              btn._tippy.enable();
            }
          }
        });
      });
    });
  });

  // Mirror <tag-input> selections into hidden tags[] inputs, then submit.
  const tagInputs = container.querySelectorAll ? [...container.querySelectorAll("tag-input")] : [];

  onceEach(tagInputs, "ingredient-search-tag-filter", (tagInput) => {
    tagInput.addEventListener("update:model-value", (event) => {
      const tags = event.detail[0] || [];
      const form = tagInput.closest("form");
      if (!form) return;

      const hiddenContainer = form.querySelector("[data-tag-hidden-inputs]");
      if (hiddenContainer) {
        hiddenContainer.innerHTML = "";
        tags.forEach((tag) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = "tags[]";
          input.value = tag.name;
          hiddenContainer.appendChild(input);
        });
      }

      setTagInputSelection(
        tagInput,
        tags.map((t) => t.name)
      );
      safeSubmitForm(form);
    });
  });

  // Mirror <ingredient-type-filter> selections into hidden ingredient_types[] inputs, then submit.
  const typeFilters = container.querySelectorAll
    ? [...container.querySelectorAll("ingredient-type-filter")]
    : [];

  onceEach(typeFilters, "ingredient-search-type-filter", (typeFilter) => {
    typeFilter.addEventListener("update:model-value", (event) => {
      const types = event.detail[0] || [];
      const form = typeFilter.closest("form");
      if (!form) return;

      const hiddenContainer = form.querySelector("[data-ingredient-type-hidden-inputs]");
      if (hiddenContainer) {
        hiddenContainer.innerHTML = "";
        types.forEach((typeKey) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = "ingredient_types[]";
          input.value = typeKey;
          hiddenContainer.appendChild(input);
        });
      }

      safeSubmitForm(form);
    });
  });

  /*
   * Auto-submit search when focusing the search field if the current scope allows blank queries.
   * This provides immediate results for filters like "Ingredients I've used in recipes".
   */
  const searchForms = container.querySelectorAll
    ? [...container.querySelectorAll("#search-form")]
    : [];
  if (container.matches?.("#search-form")) {
    searchForms.push(container);
  }

  onceEach(searchForms, "ingredient-search-auto-submit", (searchForm) => {
    const searchInput = searchForm.querySelector("input[type=search]");
    if (!searchInput) return;

    let hasAutoSubmittedOnFocus = false;

    searchInput.addEventListener("focus", () => {
      if (hasAutoSubmittedOnFocus) return;

      const filterContainer = searchForm.querySelector(filterSelector);
      if (!filterContainer) return;

      const scopesAllowingBlank = JSON.parse(filterContainer.dataset.scopesAllowingBlank || "[]");
      const currentScope = filterContainer.dataset.currentScope;
      const trimmed = searchInput.value.trim();

      if (trimmed || scopesAllowingBlank.includes(currentScope)) {
        hasAutoSubmittedOnFocus = true;
        safeSubmitForm(searchForm);
      }
    });

    searchInput.addEventListener("input", () => {
      hasAutoSubmittedOnFocus = false;
    });
  });
};

export { initializeIngredientSearch };
